import type { WalletPayload } from './types';

interface EncryptedWalletPack {
  version: number;
  kdf: 'PBKDF2-SHA256';
  iterations: number;
  salt: string;
  iv: string;
  aad: string;
  ciphertext: string;
}

function bytesFromBase64(value: string): Uint8Array {
  const binary = atob(value);
  const out = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) out[i] = binary.charCodeAt(i);
  return out;
}

/**
 * Web Crypto's DOM typings require an ArrayBuffer-backed BufferSource.
 * Copying into a fresh Uint8Array guarantees we never pass a view backed by
 * SharedArrayBuffer/ArrayBufferLike under stricter TypeScript lib.dom types.
 */
function ownedArrayBuffer(value: Uint8Array): ArrayBuffer {
  return Uint8Array.from(value).buffer;
}

function utf8Buffer(value: string): ArrayBuffer {
  return ownedArrayBuffer(new TextEncoder().encode(value));
}

function base64FromBytes(value: ArrayBuffer | Uint8Array): string {
  const bytes = value instanceof Uint8Array ? value : new Uint8Array(value);
  let binary = '';
  for (let i = 0; i < bytes.length; i += 1) binary += String.fromCharCode(bytes[i]);
  return btoa(binary);
}

function sanitizeWalletPayload(payload: WalletPayload): WalletPayload {
  return {
    ...payload,
    tickets: payload.tickets.filter((ticket) => ticket.eventId !== 'orsay'),
  };
}

async function fetchPack(): Promise<EncryptedWalletPack> {
  const response = await fetch('/wallet-pack.enc.json', { cache: 'no-store' });
  if (!response.ok) throw new Error('No se pudo abrir el paquete seguro del Wallet.');
  return response.json() as Promise<EncryptedWalletPack>;
}

export async function deriveWalletKey(passphrase: string, pack: EncryptedWalletPack): Promise<CryptoKey> {
  const material = await crypto.subtle.importKey(
    'raw',
    utf8Buffer(passphrase.trim()),
    'PBKDF2',
    false,
    ['deriveKey'],
  );
  return crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      hash: 'SHA-256',
      salt: ownedArrayBuffer(bytesFromBase64(pack.salt)),
      iterations: pack.iterations,
    },
    material,
    { name: 'AES-GCM', length: 256 },
    true,
    ['decrypt'],
  );
}

export async function decryptWalletWithPassphrase(passphrase: string): Promise<{ payload: WalletPayload; keyBase64: string }> {
  const pack = await fetchPack();
  const key = await deriveWalletKey(passphrase, pack);
  const plain = await crypto.subtle.decrypt(
    {
      name: 'AES-GCM',
      iv: ownedArrayBuffer(bytesFromBase64(pack.iv)),
      additionalData: utf8Buffer(pack.aad),
    },
    key,
    ownedArrayBuffer(bytesFromBase64(pack.ciphertext)),
  );
  const rawKey = await crypto.subtle.exportKey('raw', key);
  const payload = sanitizeWalletPayload(JSON.parse(new TextDecoder().decode(plain)) as WalletPayload);
  return { payload, keyBase64: base64FromBytes(rawKey) };
}

export async function decryptWalletWithStoredKey(keyBase64: string): Promise<WalletPayload> {
  const pack = await fetchPack();
  const key = await crypto.subtle.importKey(
    'raw',
    ownedArrayBuffer(bytesFromBase64(keyBase64)),
    { name: 'AES-GCM' },
    false,
    ['decrypt'],
  );
  const plain = await crypto.subtle.decrypt(
    {
      name: 'AES-GCM',
      iv: ownedArrayBuffer(bytesFromBase64(pack.iv)),
      additionalData: utf8Buffer(pack.aad),
    },
    key,
    ownedArrayBuffer(bytesFromBase64(pack.ciphertext)),
  );
  return sanitizeWalletPayload(JSON.parse(new TextDecoder().decode(plain)) as WalletPayload);
}
